import { toSavingsRates } from 'features/accounts/helpers/savingsRates.helpers';
import {
  AgreementsData,
  Data,
  GetTokenResult,
  TransferData,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import {
  VerifyAttributeOtpRequest,
  VerifyAttributeOtpResponse,
} from 'features/v2a/receiver/hooks/usePaymentOtp';
import unauthenticatedFetch from 'services/rest/unauthenticatedFetch';
import { getReceiverV2ALambda } from 'shared/providers/environment';
import { Environment } from 'shared/providers/environment.constants';
import { ClientError, isBaseNetworkError } from 'shared/utils/errors';

export type SavingsRatesData = NonNullable<
  Parameters<typeof toSavingsRates>[0]
>;

interface ErrorResponse {
  errorCode: string;
  title: string;
  message: string;
}

export const getToken = async (
  paymentToken: string,
  currentEnv: Environment,
  iovationToken: string,
  varoSessionId: string,
) => {
  const baseURL = getReceiverV2ALambda(currentEnv);

  try {
    const response = await unauthenticatedFetch(
      `${baseURL}tokens/${paymentToken}`,
      {
        method: 'POST',
        body: JSON.stringify({
          aToken: iovationToken,
        }),
        headers: {
          'x-varo-session': varoSessionId,
          'Content-Type': 'application/json',
        },
      },
    );
    return response.json() as Promise<GetTokenResult>;
  } catch (error) {
    if (isBaseNetworkError(error)) {
      return undefined;
    }
    throw error;
  }
};

export const fetchAgreement = async (
  token: string,
  agreementType: string,
  currentEnv: Environment,
  varoSessionId: string,
) => {
  const baseURL = getReceiverV2ALambda(currentEnv);

  try {
    const response = await unauthenticatedFetch(
      `${baseURL}agreements?type=${agreementType}`,
      {
        headers: { 'x-varo-token': token, 'x-varo-session': varoSessionId },
      },
    );
    return response.json() as Promise<AgreementsData>;
  } catch (error) {
    if (isBaseNetworkError(error)) {
      return undefined;
    }
    throw error;
  }
};

export const fetchSavingsRates = async (
  currentEnv: Environment,
  varoSessionId: string,
  signal?: AbortSignal,
) => {
  const baseURL = getReceiverV2ALambda(currentEnv);

  const response = await unauthenticatedFetch(`${baseURL}savings-rates`, {
    cache: 'no-cache',
    headers: { 'x-varo-session': varoSessionId },
    signal,
  });
  return response.json() as Promise<SavingsRatesData>;
};

export const transferData = async (
  values: Data,
  token: GetTokenResult,
  currentEnv: Environment,
  varoSessionId: string,
) => {
  const baseURL = getReceiverV2ALambda(currentEnv);

  try {
    const response = await unauthenticatedFetch(
      `${baseURL}transfers/${token.transferId}`,
      {
        method: 'PUT',
        headers: {
          'x-varo-token': token.token,
          'x-varo-session': varoSessionId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...values }),
      },
    );
    return response.json() as Promise<TransferData>;
  } catch (error) {
    if (error instanceof ClientError) {
      throw new Error(JSON.stringify(error.responseBody));
    }
    throw error;
  }
};

export const sendOTPRequest = async (
  lambdaUrl: string,
  varoSessionId: string,
  data: VerifyAttributeOtpRequest,
) => {
  const response = await unauthenticatedFetch(`${lambdaUrl}/verify-attribute`, {
    method: 'POST',
    cache: 'no-cache',
    body: JSON.stringify({
      ...data,
    }),
    headers: {
      'x-varo-session': varoSessionId,
      'Content-Type': 'application/json',
    },
  });
  return response.json() as Promise<VerifyAttributeOtpResponse | ErrorResponse>;
};

export const resendOTPRequest = async (
  lambdaUrl: string,
  varoSessionId: string,
  data: { challengeId: string },
) => {
  const response = await unauthenticatedFetch(`${lambdaUrl}/resend-otp`, {
    method: 'POST',
    cache: 'no-cache',
    body: JSON.stringify({
      ...data,
    }),
    headers: {
      'x-varo-session': varoSessionId,
      'Content-Type': 'application/json',
    },
  });
  return response.json() as Promise<VerifyAttributeOtpResponse | ErrorResponse>;
};
